import { useEffect, useId, useRef, useState } from 'react';
import { CheckIcon, Pencil2Icon } from '@radix-ui/react-icons';
import { userErrorMessage } from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import { nextRequestId, WebApiError } from '../services/http';

type TotalResult = Awaited<ReturnType<EbookClient['setQuestionTotal']>>;
type Textbook = Awaited<ReturnType<EbookClient['list']>>[number];

export function TextbookQuestionTotal({
  client,
  item,
  busy,
  onSaved,
  onRefresh,
}: {
  readonly client: EbookClient;
  readonly item: Textbook;
  readonly busy: boolean;
  readonly onSaved: (result: TotalResult) => void;
  readonly onRefresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const pending = useRef(false);
  const retry = useRef<{ version: number; total: number; requestId: string } | null>(null);
  const count = item.authoringProgress?.arrangedCount ?? 0;
  const total = item.authoringProgress?.totalCount;

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);
  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2000);
    return () => window.clearTimeout(timer);
  }, [saved]);
  useEffect(() => {
    setConflict(false);
    setError('');
  }, [item.stateVersion]);

  function close() {
    setEditing(false);
    setError('');
    setConflict(false);
    requestAnimationFrame(() => trigger.current?.focus());
  }
  async function save() {
    if (pending.current || busy || conflict) return;
    const number = Number(value.trim());
    if (!/^\d+$/.test(value.trim()) || !Number.isSafeInteger(number) || number <= 0) {
      setError('请输入大于 0 的整数。');
      input.current?.focus();
      return;
    }
    if (number < count) {
      setError(`题目总数不能少于已编排的 ${count} 题。`);
      input.current?.focus();
      return;
    }
    if (retry.current?.version !== item.stateVersion || retry.current?.total !== number)
      retry.current = {
        version: item.stateVersion,
        total: number,
        requestId: nextRequestId('ebook-total'),
      };
    pending.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await client.setQuestionTotal(
        item.id,
        item.stateVersion,
        number,
        retry.current.requestId,
      );
      onSaved(result);
      if (active.current) {
        close();
        setSaved(true);
      }
      retry.current = null;
    } catch (reason) {
      if (active.current) {
        const stale = reason instanceof WebApiError && reason.status === 409;
        setConflict(stale);
        setError(
          stale
            ? '教材已在其他窗口更新，请刷新教材后再保存。'
            : userErrorMessage(reason, '保存失败，请重试。'),
        );
      }
    } finally {
      pending.current = false;
      if (active.current) setSaving(false);
    }
  }

  return (
    <div className="textbook-question-total">
      {editing ? (
        <form
          aria-label="设置题目总数"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !saving) {
              event.preventDefault();
              close();
            }
          }}
        >
          <label htmlFor={inputId}>题目总数</label>
          <div className="textbook-question-total__controls">
            <div className="textbook-question-total__input">
              <input
                ref={input}
                id={inputId}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={value}
                disabled={saving || busy}
                aria-invalid={!!error}
                aria-describedby={`${inputId}-help${error ? ` ${inputId}-error` : ''}`}
                onChange={(event) => {
                  setValue(event.target.value);
                  if (!conflict) setError('');
                }}
              />
              <span aria-hidden="true">题</span>
            </div>
            <button
              className="textbook-question-total__save"
              type="submit"
              disabled={saving || busy || conflict}
            >
              {saving ? '保存中…' : '保存'}
            </button>
            <button
              className="textbook-question-total__cancel"
              type="button"
              disabled={saving}
              onClick={close}
            >
              取消
            </button>
          </div>
          <p id={`${inputId}-help`} className="textbook-question-total__help">
            填写整本教材的题目数量，用于计算编排完成度。
          </p>
          {error && (
            <p id={`${inputId}-error`} className="textbook-question-total__error" role="alert">
              {error}
            </p>
          )}
          {conflict && (
            <button type="button" disabled={busy} onClick={onRefresh}>
              刷新教材
            </button>
          )}
        </form>
      ) : (
        <div className="textbook-question-total__summary">
          <button
            ref={trigger}
            type="button"
            className="textbook-question-total__trigger"
            disabled={busy}
            aria-label={total ? `修改题目总数，当前 ${total} 题` : '设置题目总数'}
            onClick={() => {
              setValue(total == null ? '' : String(total));
              setSaved(false);
              setEditing(true);
            }}
          >
            {total == null ? (
              '题目总数 · 设置'
            ) : (
              <>
                题目总数：<strong>{total}</strong> 题 <Pencil2Icon aria-hidden="true" />
              </>
            )}
          </button>
          <span className="textbook-question-total__saved" role="status">
            {saved && (
              <>
                <CheckIcon aria-hidden="true" />
                已保存
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
